import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

// index.tsx registers both plugins for the app; registering them here keeps the helper usable on
// its own (and under jest, which never runs index.tsx). dayjs.extend is idempotent.
dayjs.extend(utc);
dayjs.extend(timezone);

// The zone every admin screen reads dates in — dayjs's default, set once in index.tsx. Named
// explicitly so a deadline formats the same way in a test as it does on screen.
const DISPLAY_TZ = "Europe/Belgrade";

/**
 * How long a card refund is held before it is paid out. Fixed by the backend — the panel only
 * previews the deadline with it, it never sends it.
 */
export const REFUND_GRACE_HOURS = 24;

// The deadline to show before the cancellation has been sent. The server writes the real stamp,
// so this is the admin's clock plus the window and the copy around it says "rreth".
export const previewRefundDeadlineUtc = (now: Date | number = Date.now()): string =>
  dayjs(now).add(REFUND_GRACE_HOURS, "hour").toISOString();

// A UTC stamp as the exact local date and time the admin reads everywhere else. Null — never a
// placeholder — when there is no stamp or it cannot be parsed, so the caller drops the whole
// sentence rather than printing a deadline that says nothing.
export const formatRefundDeadline = (value?: string | null): string | null => {
  if (!value) return null;
  const at = dayjs.utc(value);
  if (!at.isValid()) return null;
  return at.tz(DISPLAY_TZ).format("DD.MM.YYYY HH:mm");
};

export interface IRefundGraceFields {
  refundScheduledForUtc?: string | null;
}

/** Where a cancellation's card refund stands right now. */
export type RefundGraceState =
  /** No card refund on this cancellation — Gjirafa Money only, or no refund at all. */
  | "none"
  /** Still held: the money has not moved and the booking can still be reconfirmed. */
  | "open"
  /** The deadline passed; the payout is on its way. */
  | "elapsed";

export const refundGraceState = (
  row: IRefundGraceFields,
  now: Date | number = Date.now()
): RefundGraceState => {
  const scheduled = row.refundScheduledForUtc
    ? dayjs.utc(row.refundScheduledForUtc)
    : null;
  if (!scheduled || !scheduled.isValid()) return "none";
  return scheduled.valueOf() > dayjs(now).valueOf() ? "open" : "elapsed";
};
